import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../../i18n/localeUtils'
import { FormTable, FormTableRow } from './FormTable'
import { RegisteredDatePicker } from '../../../components/fields/DatePicker'
import { ShouldShow } from '../../../components/entities/IssueType'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { Link } from 'react-router'
import * as datefns from 'date-fns'
import { useFormContext, useWatch } from 'react-hook-form'
import Stack from '@mui/material/Stack'
// AI suggestion integration:
// - `ExtractionSuggestionControls` reads visibility from context and renders the compact suggestion hint, a "Use" button
//   and a popover with source details. These pieces are used below to offer
//   suggested dates that users can accept into the form.
import { FieldProvenance, usePressReleasesExtractions } from './issue-form/PressReleasesExtractionsContext'
import ExtractionSuggestionControls from './issue-form/ExtractionSuggestionControls'
import { FormIssueEntity } from '../types/FormIssueEntity'
import { SourceEntity } from 'types/SourceEntity'
import { ImportantDatesForm } from '../types/ImportantDatesForm'
import { DateValidationError } from '@mui/x-date-pickers/models'

const getIncRightsMinDate = ({ publicationSourcePublishedDate, generalMeetingDate }) => {
    if (!publicationSourcePublishedDate && !generalMeetingDate) return
    const gmd = new Date(generalMeetingDate)
    const pspd = datefns.addDays(new Date(publicationSourcePublishedDate), 1)

    if (!publicationSourcePublishedDate) return gmd
    if (!generalMeetingDate) return pspd

    return datefns.max([gmd, pspd])
}

const validateDates = (formValues) => {
    let isValid = true

    if (
        formValues.generalMeetingDate &&
        formValues.publicationDate &&
        formValues.incRightsDate &&
        datefns.isBefore(
            new Date(formValues.incRightsDate),
            getIncRightsMinDate({
                publicationSourcePublishedDate: formValues.publicationDate,
                generalMeetingDate: formValues.generalMeetingDate,
            }),
        )
    ) {
        isValid = false
    }
    if (
        formValues.incRightsDate &&
        formValues.exRightsDate &&
        datefns.isBefore(new Date(formValues.exRightsDate), datefns.addDays(new Date(formValues.incRightsDate), 1))
    ) {
        isValid = false
    }
    if (
        formValues.exRightsDate &&
        formValues.recordDate &&
        datefns.isBefore(new Date(formValues.recordDate), datefns.addDays(new Date(formValues.exRightsDate), 1))
    ) {
        isValid = false
    }
    if (
        formValues.recordDate &&
        formValues.rightsStartDate &&
        datefns.isBefore(new Date(formValues.rightsStartDate), new Date(formValues.recordDate))
    ) {
        isValid = false
    }
    if (
        formValues.recordDate &&
        formValues.subStartDate &&
        datefns.isBefore(new Date(formValues.subStartDate), datefns.addDays(new Date(formValues.recordDate), 1))
    ) {
        isValid = false
    }
    if (
        formValues.rightsStartDate &&
        formValues.rightsEndDate &&
        datefns.isBefore(
            new Date(formValues.rightsEndDate),
            datefns.addDays(datefns.max([new Date(formValues.subStartDate), new Date(formValues.rightsStartDate)]), 1),
        )
    ) {
        isValid = false
    }
    if (
        formValues.rightsEndDate &&
        formValues.subEndDate &&
        datefns.isBefore(
            new Date(formValues.subEndDate),
            datefns.addDays(datefns.max([new Date(formValues.subStartDate), new Date(formValues.rightsEndDate)]), 1),
        )
    ) {
        isValid = false
    }

    return isValid
}

const FIELD_TO_IMPORTANT_DATE_KEY: Record<keyof ImportantDatesForm, string> = {
    generalMeetingDate: 'general_meeting_date',
    incRightsDate: 'inc_rights_date',
    exRightsDate: 'ex_rights_date',
    recordDate: 'record_date',
    rightsStartDate: 'rights_start_date',
    subStartDate: 'sub_start_date',
    rightsEndDate: 'rights_end_date',
    subEndDate: 'sub_end_date',
    ipoTradingDate: 'ipo_trading_date',
} as const

type FieldToImportantDateKey = typeof FIELD_TO_IMPORTANT_DATE_KEY
type AiSuggestionKeys = FieldToImportantDateKey[keyof FieldToImportantDateKey]

// Read both camelCase and snake_case so this UI keeps working while payload formats differ.
const getFieldValueByName = <T extends string | null | FieldProvenance>(
    fieldName: keyof ImportantDatesForm,
    valuesByField: Record<AiSuggestionKeys, T>,
): T => {
    if (!valuesByField) return null
    if (valuesByField[fieldName] !== undefined) return valuesByField[fieldName]

    const importantDateKey = FIELD_TO_IMPORTANT_DATE_KEY[fieldName]
    if (!importantDateKey) return null
    return valuesByField[importantDateKey] ?? null
}

// Wrapper around date pickers that displays an AI suggestion and provenance
// beside the control. When the user clicks "Use" we call `setValue` so the
// suggestion flows through react-hook-form (dirty/touched/validation).
function DatePickerWithHint({
    fieldName,
    fieldValue,
    children,
    suggestionImportantDates,
    suggestionProvenanceByField,
}: {
    fieldName: keyof ImportantDatesForm
    fieldValue?: string | null
    children: ReactNode
    suggestionImportantDates: Record<AiSuggestionKeys, string | null>
    suggestionProvenanceByField: Record<AiSuggestionKeys, FieldProvenance>
}) {
    const { setValue } = useFormContext()
    const { language: locale } = useAppLocale()
    const suggestionValue = getFieldValueByName(fieldName, suggestionImportantDates)
    const suggestionText = suggestionValue != null ? `AI förslag: ${suggestionValue}` : null
    const provenance = getFieldValueByName(fieldName, suggestionProvenanceByField) || {}
    const sourcePages = provenance.sourcePages ?? []
    const quote = provenance.quote || null

    // Use setValue so AI suggestions go through normal form flow (dirty/touched/validation).
    const applySuggestion = () => {
        if (!suggestionValue) return
        setValue(fieldName, suggestionValue, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        })
    }

    const formattedFieldValue = fieldValue ? formatDateYYYYMMDD(fieldValue, { locale }) : null
    const formattedSuggestionValue = suggestionValue ? formatDateYYYYMMDD(suggestionValue, { locale }) : null

    return (
        <Stack spacing={0.75}>
            <Stack
                direction='row'
                spacing={7}
                sx={{
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                {children}
                <ExtractionSuggestionControls
                    suggestionText={suggestionText}
                    onUse={applySuggestion}
                    pressReleaseId={provenance.pressReleaseId}
                    sourcePages={sourcePages}
                    quote={quote}
                    warning={
                        formattedFieldValue !== null &&
                        formattedSuggestionValue !== null &&
                        formattedFieldValue !== formattedSuggestionValue
                            ? 'AI föreslår ett annat värde'
                            : null
                    }
                />
            </Stack>
        </Stack>
    )
}

function ImportantDates({
    issue,
    publicationSource,
    voteDisableSave,
}: {
    issue: FormIssueEntity
    publicationSource: SourceEntity | null
    voteDisableSave: (f: string, v: boolean) => void
}) {
    /*
    This function handles the important dates section of the admin issue form, which includes:
    - Displaying the publication date (with source link if available)
    - Date pickers for general meeting date, inc/ex rights dates, record date, rights
    */
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()
    const formValues = useWatch<FormIssueEntity>()
    const { suggestionImportantDates, suggestionProvenanceByField } = usePressReleasesExtractions()

    const [validDates, setValidDates] = React.useState(validateDates(formValues))

    // const [isSaveDisabled, setIsSaveDisabled] = React.useState(true)
    const [error, setError] = React.useState({})
    const i18nPrefix = 'entities.issue.fields.'
    React.useEffect(() => {
        if (voteDisableSave) {
            const valid = validateDates(formValues)
            if (valid !== validDates) {
                console.log(`valid: ${valid}, validDates: ${validDates}`)
                setValidDates(valid)
                voteDisableSave('dates', !valid)
            }
        }
    }, [formValues, validDates, voteDisableSave])

    let publicationDate = (
        <>{issue.publicationDate ? formatDateYYYYMMDD(issue.publicationDate, { locale }) : 'Saknas'}</>
    )
    if (publicationSource) {
        publicationDate = (
            <>
                {formatDateYYYYMMDD(publicationSource.publishedDate, { locale })} {': '}
                <Link to={publicationSource.sourceUrl} target='_blank'>
                    {publicationSource.title}
                </Link>
            </>
        )
    }

    const handleError = (field: keyof ImportantDatesForm, newError: DateValidationError) => {
        console.log(`Error in ${field}: ${newError}`)
        setError({ ...error, [field]: newError })
    }

    return (
        <FormTable>
            <ShouldShow issueType={issue.type} issueField='publicationDate'>
                <FormTableRow label='Publiceringsdatum'>{publicationDate}</FormTableRow>
            </ShouldShow>

            <ShouldShow key={'generalMeetingDate'} issueType={issue.type} issueField={'generalMeetingDate'}>
                <FormTableRow key={'generalMeetingDate'} label={t(`${i18nPrefix}generalMeetingDate`)}>
                    <DatePickerWithHint
                        fieldName='generalMeetingDate'
                        fieldValue={formValues.generalMeetingDate}
                        suggestionImportantDates={suggestionImportantDates}
                        suggestionProvenanceByField={suggestionProvenanceByField}
                    >
                        <RegisteredDatePicker
                            name={'generalMeetingDate'}
                            i18nLabel={`${i18nPrefix}generalMeetingDate`}
                            minDate={datefns.addDays(new Date(publicationSource?.publishedDate), 1)}
                            referenceDate={
                                publicationSource?.publishedDate
                                    ? datefns.addDays(new Date(publicationSource?.publishedDate), 1)
                                    : new Date()
                            }
                            onError={(newError) => handleError('generalMeetingDate', newError)}
                        />
                    </DatePickerWithHint>
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'incRightsDate'} issueType={issue.type} issueField={'incRightsDate'}>
                <FormTableRow key={'incRightsDate'} label={t(`${i18nPrefix}incRightsDate`)}>
                    <DatePickerWithHint
                        fieldName='incRightsDate'
                        fieldValue={formValues.incRightsDate}
                        suggestionImportantDates={suggestionImportantDates}
                        suggestionProvenanceByField={suggestionProvenanceByField}
                    >
                        <RegisteredDatePicker
                            name={'incRightsDate'}
                            i18nLabel={`${i18nPrefix}incRightsDate`}
                            minDate={getIncRightsMinDate({
                                publicationSourcePublishedDate: publicationSource?.publishedDate,
                                generalMeetingDate: formValues.generalMeetingDate,
                            })}
                            referenceDate={
                                formValues.generalMeetingDate || publicationSource?.publishedDate
                                    ? getIncRightsMinDate({
                                          publicationSourcePublishedDate: publicationSource?.publishedDate,
                                          generalMeetingDate: formValues.generalMeetingDate,
                                      })
                                    : new Date()
                            }
                            onError={(newError) => handleError('incRightsDate', newError)}
                        />
                    </DatePickerWithHint>
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'exRightsDate'} issueType={issue.type} issueField={'exRightsDate'}>
                <FormTableRow key={'exRightsDate'} label={t(`${i18nPrefix}exRightsDate`)}>
                    <DatePickerWithHint
                        fieldName='exRightsDate'
                        fieldValue={formValues.exRightsDate}
                        suggestionImportantDates={suggestionImportantDates}
                        suggestionProvenanceByField={suggestionProvenanceByField}
                    >
                        <RegisteredDatePicker
                            name={'exRightsDate'}
                            i18nLabel={`${i18nPrefix}exRightsDate`}
                            minDate={datefns.addDays(new Date(formValues.incRightsDate), 1)}
                            referenceDate={
                                formValues.incRightsDate
                                    ? datefns.addDays(new Date(formValues.incRightsDate), 1)
                                    : new Date()
                            }
                            onError={(newError) => handleError('exRightsDate', newError)}
                        />
                    </DatePickerWithHint>
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'recordDate'} issueType={issue.type} issueField={'recordDate'}>
                <FormTableRow key={'recordDate'} label={t(`${i18nPrefix}recordDate`)}>
                    <DatePickerWithHint
                        fieldName='recordDate'
                        fieldValue={formValues.recordDate}
                        suggestionImportantDates={suggestionImportantDates}
                        suggestionProvenanceByField={suggestionProvenanceByField}
                    >
                        <RegisteredDatePicker
                            name={'recordDate'}
                            i18nLabel={`${i18nPrefix}recordDate`}
                            minDate={datefns.addDays(new Date(formValues.exRightsDate), 1)}
                            referenceDate={
                                formValues.exRightsDate
                                    ? datefns.addDays(new Date(formValues.exRightsDate), 1)
                                    : new Date()
                            }
                            onError={(newError) => handleError('recordDate', newError)}
                        />
                    </DatePickerWithHint>
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'rightsStartDate'} issueType={issue.type} issueField={'rightsStartDate'}>
                <FormTableRow key={'rightsStartDate'} label={t(`${i18nPrefix}rightsStartDate`)}>
                    <DatePickerWithHint
                        fieldName='rightsStartDate'
                        fieldValue={formValues.rightsStartDate}
                        suggestionImportantDates={suggestionImportantDates}
                        suggestionProvenanceByField={suggestionProvenanceByField}
                    >
                        <RegisteredDatePicker
                            name={'rightsStartDate'}
                            i18nLabel={`${i18nPrefix}rightsStartDate`}
                            minDate={new Date(formValues.recordDate)}
                            referenceDate={formValues.recordDate ? new Date(formValues.recordDate) : new Date()}
                            onError={(newError) => handleError('rightsStartDate', newError)}
                        />
                    </DatePickerWithHint>
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'subStartDate'} issueType={issue.type} issueField={'subStartDate'}>
                <FormTableRow key={'subStartDate'} label={t(`${i18nPrefix}subStartDate`)}>
                    <DatePickerWithHint
                        fieldName='subStartDate'
                        fieldValue={formValues.subStartDate}
                        suggestionImportantDates={suggestionImportantDates}
                        suggestionProvenanceByField={suggestionProvenanceByField}
                    >
                        <RegisteredDatePicker
                            name={'subStartDate'}
                            i18nLabel={`${i18nPrefix}subStartDate`}
                            minDate={datefns.addDays(new Date(formValues.recordDate), 1)}
                            referenceDate={
                                formValues.recordDate ? datefns.addDays(new Date(formValues.recordDate), 1) : new Date()
                            }
                            onError={(newError) => handleError('subStartDate', newError)}
                        />
                    </DatePickerWithHint>
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'rightsEndDate'} issueType={issue.type} issueField={'rightsEndDate'}>
                <FormTableRow key={'rightsEndDate'} label={t(`${i18nPrefix}rightsEndDate`)}>
                    <DatePickerWithHint
                        fieldName='rightsEndDate'
                        fieldValue={formValues.rightsEndDate}
                        suggestionImportantDates={suggestionImportantDates}
                        suggestionProvenanceByField={suggestionProvenanceByField}
                    >
                        <RegisteredDatePicker
                            name={'rightsEndDate'}
                            i18nLabel={`${i18nPrefix}rightsEndDate`}
                            minDate={datefns.addDays(
                                datefns.max([new Date(formValues.subStartDate), new Date(formValues.rightsStartDate)]),
                                1,
                            )}
                            referenceDate={
                                formValues.subStartDate || formValues.rightsStartDate
                                    ? datefns.addDays(
                                          datefns.max([
                                              new Date(formValues.subStartDate),
                                              new Date(formValues.rightsStartDate),
                                          ]),
                                          1,
                                      )
                                    : new Date()
                            }
                            onError={(newError) => handleError('rightsEndDate', newError)}
                        />
                    </DatePickerWithHint>
                </FormTableRow>
            </ShouldShow>

            <ShouldShow key={'subEndDate'} issueType={issue.type} issueField={'subEndDate'}>
                <FormTableRow key={'subEndDate'} label={t(`${i18nPrefix}subEndDate`)}>
                    <DatePickerWithHint
                        fieldName='subEndDate'
                        fieldValue={formValues.subEndDate}
                        suggestionImportantDates={suggestionImportantDates}
                        suggestionProvenanceByField={suggestionProvenanceByField}
                    >
                        <RegisteredDatePicker
                            name={'subEndDate'}
                            i18nLabel={`${i18nPrefix}subEndDate`}
                            minDate={datefns.addDays(
                                datefns.max([new Date(formValues.subStartDate), new Date(formValues.rightsEndDate)]),
                                1,
                            )}
                            referenceDate={
                                formValues.subStartDate || formValues.rightsEndDate
                                    ? datefns.addDays(
                                          datefns.max([
                                              new Date(formValues.subStartDate),
                                              new Date(formValues.rightsEndDate),
                                          ]),
                                          1,
                                      )
                                    : new Date()
                            }
                            onError={(newError) => handleError('subEndDate', newError)}
                        />
                    </DatePickerWithHint>
                </FormTableRow>
            </ShouldShow>
            <ShouldShow key={'ipoTradingDate'} issueType={issue.type} issueField={'ipoTradingDate'}>
                <FormTableRow key={'ipoTradingDate'} label={t(`${i18nPrefix}ipoTradingDate`)}>
                    <DatePickerWithHint
                        fieldName='ipoTradingDate'
                        fieldValue={formValues.ipoTradingDate}
                        suggestionImportantDates={suggestionImportantDates}
                        suggestionProvenanceByField={suggestionProvenanceByField}
                    >
                        <RegisteredDatePicker
                            name={'ipoTradingDate'}
                            i18nLabel={`${i18nPrefix}ipoTradingDate`}
                            minDate={datefns.addDays(
                                datefns.max([new Date(formValues.subStartDate), new Date(formValues.rightsEndDate)]),
                                1,
                            )}
                            referenceDate={
                                formValues.subStartDate || formValues.rightsEndDate
                                    ? datefns.addDays(
                                          datefns.max([
                                              new Date(formValues.subStartDate),
                                              new Date(formValues.rightsEndDate),
                                          ]),
                                          1,
                                      )
                                    : new Date()
                            }
                            onError={(newError) => handleError('ipoTradingDate', newError)}
                        />
                    </DatePickerWithHint>
                </FormTableRow>
            </ShouldShow>
        </FormTable>
    )
}

export default ImportantDates
